import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { Doughnut } from "react-chartjs-2";
import { Progress, Skeleton } from "antd";
import BUDGET_API from "../../apis/budget.api";
import DEATH_API from "../../apis/death.api";
import { MODULE_API } from "../../apis";
import budgetHomeIcon from "../../assets/images/savings.png";
import budgetIcon from "../../assets/images/performance.png";
import { ChartColors } from "../../components/ChartColors";
import "../Budget/BudgetMain.css"; // Reuse existing styles
import "./BudgetReportDashboard.css";

const BudgetSummarySkeleton = () => (
  <div className="budget-summary-skeleton" aria-hidden="true" style={{ padding: "10px" }}>
    <div className="budget-summary-skeleton__left">
      <Skeleton active avatar title={false} paragraph={false} />
    </div>
    <div className="budget-summary-skeleton__right">
      <Skeleton active title={{ width: "42%" }} paragraph={false} />
      <Skeleton active title={{ width: "58%" }} paragraph={false} />
      <Skeleton active title={{ width: "70%" }} paragraph={false} />
      <Skeleton active title={{ width: "36%" }} paragraph={false} />
      <Skeleton active title={{ width: "70%" }} paragraph={false} />
    </div>
  </div>
);

const BudgetListSkeleton = ({ rows = 4 }) => (
  <div className="budget-categories-skeleton" aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div className="budget-categories-skeleton__row" key={index}>
        <div className="budget-categories-skeleton__icon" />
        <div className="budget-categories-skeleton__content">
          <div className="budget-categories-skeleton__header">
            <div className="budget-categories-skeleton__line budget-categories-skeleton__line--title" />
            <div className="budget-categories-skeleton__line budget-categories-skeleton__line--value" />
          </div>
          <div className="budget-categories-skeleton__progress" />
          <div className="budget-categories-skeleton__line budget-categories-skeleton__line--footer" />
        </div>
      </div>
    ))}
  </div>
);

export const BudgetReportDashboard = () => {
  const history = useHistory();
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [budgetData, setBudgetData] = useState(null);
  const [totalBudget, setTotalBudget] = useState({ totalBudget: 0, totalTransactions: 0 });
  const [categoryBudget, setCategoryBudget] = useState([]);
  const [monthlyDataReport, setMonthlyDataReport] = useState({});
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [monthsBudgetData, setMonthsBudgetData] = useState([]);
  const [, setClientTransactionsCategories] = useState([]);
  const [, setClientTransactionsSubCategories] = useState([]);

  const getIncomeTotalMonthly = async (startDate, endDate) => {
    try {
      setIncomeLoading(true);
      let datares = await MODULE_API.fetchIncomeSum(startDate, endDate);
      console.log("monthly datares", datares);
      if (datares && datares.data) {
        const monthlyData = datares.data;
        let monthlyResponse = {
          ...datares.data,
          totalMonthly: monthlyData
            ? (+monthlyData.Active || 0) + (+monthlyData.Passive || 0)
            : 0,
        };
        setMonthlyDataReport(monthlyResponse);
      }
      setIncomeLoading(false);
    } catch (error) {
      console.log(error);
      setIncomeLoading(false);
    }
  };

  const getAllBudgetsMonthsCategory = async (startDate, endDate) => {
    try {
      let data = await BUDGET_API.fetchAllBudget(startDate, endDate, "month","false");
      if (data && data.status === 200 && data.categorized) {
        setMonthsBudgetData(data.categorized);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getTransactionFilters = async () => {
    try {
      const [categoriesRes, subCategoriesRes] = await Promise.all([
        BUDGET_API.fetchAllTransactionCatgoires(),
        BUDGET_API.fetchAllTransactionSubCatgoires(),
      ]);

      const filteredCategories =
        categoriesRes &&
        categoriesRes.status === 200 &&
        categoriesRes.data
          ? categoriesRes.data.filter(
              (item) =>
                item.category.categoryName !== "Transfer Out" &&
                item.category.categoryName !== "Credit Card Payment"
            )
          : [];

      const filteredSubCategories =
        subCategoriesRes &&
        subCategoriesRes.status === 200 &&
        subCategoriesRes.data
          ? subCategoriesRes.data.filter(
              (item) =>
                item.subCategoryCategory.categoryName !== "Transfer Out" &&
                item.subCategoryCategory.categoryName !== "Credit Card Payment" &&
                item.subCategory.name !== "Credit Card Payment"
            )
          : [];

      setClientTransactionsCategories(filteredCategories);
      setClientTransactionsSubCategories(filteredSubCategories);

      return {
        categoryIds: filteredCategories
          .filter((item) => item.enable === true)
          .map((item) => item.categoryId),
        subCategoryIds: filteredSubCategories
          .filter((item) => item.enable === true)
          .map((item) => item.subCategoryId),
      };
    } catch (error) {
      console.log(error);
      setClientTransactionsCategories([]);
      setClientTransactionsSubCategories([]);
      return {
        categoryIds: [],
        subCategoryIds: [],
      };
    }
  };

  const getMonthlyExpenses = async (startDate, endDate) => {
    try {
      setExpenseLoading(true);
      const { categoryIds, subCategoryIds } = await getTransactionFilters();
      const datares = await DEATH_API.getBudgetChartDataMonthly(startDate, endDate, {
        categoryId: categoryIds,
        subCategoryId: subCategoryIds,
        startDate,
        endDate,
      });
      const totalExpenses =
        datares && datares.total_expenses !== undefined
          ? Math.abs(parseFloat(datares.total_expenses) || 0)
          : 0;

      setMonthlyExpenses(totalExpenses);
      setExpenseLoading(false);
    } catch (error) {
      console.log(error);
      setMonthlyExpenses(0);
      setExpenseLoading(false);
    }
  };

  const fetchBudgetData = async (startDate, endDate) => {
    try {
      let data = await BUDGET_API.fetchAllBudget(startDate, endDate, "all", "false");
      if (data && data.status === 200 && data.categorized) {
        const promises = data.categorized.map((budget) => {
          const payload = {
            startDate,
            endDate,
            category: budget.categoryId,
            subCategory: budget.subCategoryId || null,
          };
          return BUDGET_API.getCalculatedBudgetTransactions(payload).then((response) => ({
            budget,
            response,
          }));
        });

        const results = await Promise.all(promises);
        const chartData = {
          usedAmounts: [],
          categoryName: [],
          amountsSums: [],
          leftAmounts: [],
        };
        const totalBudgetObj = {
          totalBudget: 0,
          totalTransactions: 0,
        };

        const structuredData = results.map(({ budget, response }) => {
          const usedAmount = response.currentMonthTransactions.totalAmount || 0;
          const totalAmount = budget.amount || 0;
          const leftAmount = totalAmount - usedAmount;

          chartData.usedAmounts.push(usedAmount);
          chartData.categoryName.push((budget.budgetCategory && budget.budgetCategory.categoryName) || "");
          chartData.amountsSums.push(totalAmount);
          chartData.leftAmounts.push(leftAmount);

          totalBudgetObj.totalBudget += totalAmount;
          totalBudgetObj.totalTransactions += usedAmount;

          return {
            categoryId: budget.categoryId,
            categoryName: (budget.budgetCategory && budget.budgetCategory.categoryName) || "",
            subCategoryName: (
              (budget.budgetCategory &&
                budget.budgetCategory.subCategories &&
                budget.budgetCategory.subCategories.find((sub) => sub.id === budget.subCategoryId)) ||
              {}
            ).name,
            usedAmount,
            leftAmount,
            totalAmount,
            image: (budget.budgetCategory && budget.budgetCategory.image) || "",
          };
        });

        setTotalBudget(totalBudgetObj);
        setBudgetData(chartData);
        setCategoryBudget(structuredData);
      }
    } catch (error) {
      console.error("Error fetching budget data:", error);
    }
  };

  const getBudgetOverviewData = async (startDate, endDate) => {
    try {
      setBudgetLoading(true);
      await Promise.all([
        fetchBudgetData(startDate, endDate),
        getAllBudgetsMonthsCategory(startDate, endDate),
      ]);
    } catch (error) {
      console.log(error);
    } finally {
      setBudgetLoading(false);
    }
  };

  const fetchData = async () => {
      const date = moment();
      const startDate = date.clone().startOf("month").format("YYYY-MM-DD");
      const endDate = date.clone().endOf("month").format("YYYY-MM-DD");
      await Promise.all([
        getIncomeTotalMonthly(startDate, endDate),
        getMonthlyExpenses(startDate, endDate),
        getBudgetOverviewData(startDate, endDate),
      ]);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const monthlyIncome = monthlyDataReport && monthlyDataReport.totalMonthly ? monthlyDataReport.totalMonthly : 0;
  const expensesVsIncome = monthlyIncome > 0 ? (monthlyExpenses / monthlyIncome) * 100 : 0;
  const isExpensesOverLimit = expensesVsIncome > 100;
  const savingsPotential = monthlyIncome - monthlyExpenses;
  const totalUncategorizedAmount =
    monthsBudgetData &&
    monthsBudgetData.reduce((sum, item) => sum + item.amount, 0);
  const totalBudgetValue1 = totalUncategorizedAmount
    ? totalUncategorizedAmount
    : 0;

  const usedAmounts =
    budgetData &&
    budgetData.usedAmounts &&
    budgetData.usedAmounts.reduce((acc, curr) => acc + curr, 0);
  const spentPercentage =
    totalBudgetValue1 > 0
      ? (((usedAmounts || 0) / totalBudgetValue1) * 100).toFixed(2)
      : "0.00";

  const dataDonught = {
    labels: budgetData ? budgetData.categoryName : [],
    datasets: [
      {
        data: budgetData ? budgetData.usedAmounts : [],
        backgroundColor: budgetData ? budgetData.categoryName.map((_, index) => ChartColors[index % ChartColors.length]) : [],
        borderWidth: 0,
      },
    ],
    text: spentPercentage && isNaN(spentPercentage) ? "0%" : `${spentPercentage}%`,
  };

  const centerTextPlugin = {
    id: "centerText",
    afterDraw: (chart) => {
      const { ctx, width, height } = chart;
      const text = chart.config.data.text;
      const sidePadding = 20;
      const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);
      ctx.font = "bold 40px Arial";
      const stringWidth = ctx.measureText(text).width;
      const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;
      const widthRatio = elementWidth / stringWidth;
      const newFontSize = Math.floor(30 * widthRatio);
      const elementHeight = chart.innerRadius * 2;
      const fontSizeToUse = Math.min(newFontSize, elementHeight);

      const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

      ctx.save();
      const fontSize = (height / 114).toFixed(2);
      ctx.font = fontSize + "em Arial";
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      ctx.font = `bold ${fontSizeToUse + 3}px Arial`;
      ctx.fillStyle = "white";
      ctx.fillText(text, centerX, centerY);
      ctx.restore();
    },
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(val || 0);
  };

  return (
    <div className="reportsMain BudgetMain">
      <div className="reportcreate" style={{ justifyContent: "space-between" }}>
        <h3 className="BudgetReportheading">Budget Report</h3>
        <button
          className="addbuttonreport"
          onClick={() => history.push("/reports/budgetDetails")}
        >
          Create Report
        </button>
      </div>

      <div className="dashboardGrid reportCardFlex">
        {/* Top Left Tile - Exact width/style matching */}
        <div className="leftCard reportCard1" style={{ width: "30%" }}>
          <div className="budget-home-icon-container">
            <div
              className="asset-home-icon"
              style={{ textAlign: "center", width: "50%" }}
            >
              <img
                style={{ width: "100%", minWidth: "100px" }}
                src={budgetHomeIcon}
                alt="budget"
              />
            </div>
            <div style={{ textAlign: "center", width: "50%" }}>
              <h3
                className="Reportheading"
                style={{ fontSize: "16px", fontWeight: "800" }}
              >
                {moment().format("MM/DD/YYYY")}
              </h3>
              <h3
                className="Reportheading"
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  marginTop: "1rem",
                }}
              >
                Monthly Budget
              </h3>
              <h3 className="Reportheading" style={{ fontWeight: "900" }}>
                $
                {totalBudgetValue1
                  ? totalBudgetValue1
                      .toFixed(2)
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : (0).toFixed(2)}
              </h3>
              <h3
                className="Reportheading"
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  marginTop: "1rem",
                }}
              >
                Spent
              </h3>
              <h3 className="Reportheading" style={{ fontWeight: "900" }}>
                {budgetLoading ? (
                  <Skeleton active title={{ width: "80%" }} paragraph={false} />
                ) : (
                  <React.Fragment>
                    ${" "}
                    {totalBudget && totalBudget.totalTransactions
                      ? totalBudget.totalTransactions
                          .toFixed(2)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : (0).toFixed(2)}
                  </React.Fragment>
                )}
              </h3>
            </div>
          </div>

          <div>
            {budgetLoading ? (
              <React.Fragment>
                <BudgetSummarySkeleton />
              </React.Fragment>
            ) : (
              budgetData &&
              budgetData.categoryName.length > 0 && (
                <Doughnut
                  pointStyle="circle"
                  data={dataDonught}
                  options={{
                    cutoutPercentage: 70,
                    responsive: true,
                    maintainAspectRatio: true,
                    legend: {
                      display:
                        budgetData && budgetData.categoryName.length < 10
                          ? true
                          : false,
                      position:
                        budgetData && budgetData.categoryName.length < 5
                          ? "left"
                          : "top",
                      labels: {
                        fontColor: "white",
                      },
                    },
                    tooltips: {
                      callbacks: {
                        label: function (tooltipItem, data) {
                          const label = data.labels[tooltipItem.index] || "";
                          const value =
                            data.datasets[tooltipItem.datasetIndex].data[
                              tooltipItem.index
                            ] || 0;
                          return `${label}: $${value
                            .toFixed(2)
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
                        },
                      },
                    },
                  }}
                  plugins={[centerTextPlugin]}
                />
              )
            )}
          </div>
        </div>

        {/* Top Right Tile - Formula Based (70%) */}
        <div className="rightCard reportCard_Formula" style={{ width: "70%" }}>
          <div className="rightCardHeader">
            <div className="briefcaseIconContainer">
              <img src={budgetIcon} alt="Briefcase" className="extraIconSmall" />
            </div>
            <h3 className="monthlyBudgetHeading">Monthly Budget Total</h3>
          </div>

          <div className="incomeExpenseGrid">
            <div className="dataBox">
              <span className="dataBoxLabel">Monthly Income</span>
              <span className="dataBoxValue">
                {incomeLoading ? <Skeleton active title={{ width: 100 }} paragraph={false} /> : formatCurrency(monthlyIncome)}
              </span>
            </div>
            <div className="dataBox">
              <span className="dataBoxLabel">Monthly Expenses</span>
              <span className="dataBoxValue expense">
                {expenseLoading ? <Skeleton active title={{ width: 100 }} paragraph={false} /> : formatCurrency(monthlyExpenses)}
              </span>
            </div>
          </div>

          <div className="progressSection">
            <div className="progressHeader">
              <span className="progressLabel">Expenses vs. Income</span>
              <span className={`progressPercent ${isExpensesOverLimit ? "progressPercentDanger" : ""}`}>{(expenseLoading || incomeLoading) ? "..." : `${expensesVsIncome.toFixed(2)}%`}</span>
            </div>
            <div className="customProgressBarAP">
              <div 
                className={`progressBarFillAP ${isExpensesOverLimit ? "progressBarFillDangerAP" : ""}`}
                style={{ width: `${Math.min(expensesVsIncome, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className="savingsBadgeAP">
            <div className="savingsLabelAP">
              <div className="dot"></div>
              Savings Potential
            </div>
            <span className="savingsValueAP">
              {(expenseLoading || incomeLoading) ? <Skeleton active title={{ width: 100 }} paragraph={false} /> : formatCurrency(savingsPotential)}
            </span>
          </div>

          <div className="updateFooterAP">
            Updated {moment().format("MM/DD/YYYY [at] hh:mm A")}
          </div>
        </div>
      </div>

      <div className="budgetOverviewHeader">
        <span>Categorized Spend {formatCurrency(totalBudget.totalBudget)} / Current Monthly Spend {formatCurrency(totalBudget.totalTransactions)}</span>
      </div>

      <div className="reportTableCard reportCard2" style={{width:"100%"}}>
        {budgetLoading ? (
            <BudgetListSkeleton rows={5} />
        ) : (
            <div className="categoryGridScroll">
            {categoryBudget.map((item, index) => {
                const percent = item.totalAmount > 0 ? (item.usedAmount / item.totalAmount) * 100 : 0;
                const left = item.totalAmount - item.usedAmount;
                
                return (
                <div key={index} className="categoryItem APRow">
                    <img 
                        src={item.image || budgetIcon} 
                        alt={item.categoryName} 
                        className="categoryIconAP" 
                    />
                    <div className="categoryContent">
                    <div className="categoryHeader">
                        <span className="categoryName">
                            {item.categoryName} {item.subCategoryName ? `(${item.subCategoryName})` : ""}
                        </span>
                        <span>
                            {formatCurrency(Math.abs(left))} {left >= 0 ? "Left" : "Over"}
                        </span>
                    </div>
                    <Progress
                        className="budgetProgress"
                        type="line"
                        strokeColor={item.usedAmount > item.totalAmount ? "#f5222d" : "#2a3da3"}
                        trailColor="#333533"
                        percent={percent}
                        showInfo={false}
                    />
                    <div className="spentInfo">
                        {formatCurrency(item.usedAmount)} of {formatCurrency(item.totalAmount)}
                    </div>
                    </div>
                </div>
                );
            })}
            </div>
        )}
      </div>
    </div>
  );
};
